/**
 * Kontora BFF – Workflow State (client-side)
 *
 * Persistiert laufende Workflow-Instanzen im LocalStorage.
 * Schema: kontora_workflow_state_v1
 * {
 *   "<workflowCode>": WorkflowInstance
 * }
 *
 * Bewusst KEINE DB-Persistierung (wie Onboarding):
 * - Workflows sind kurze, transiente Prozesse
 * - Mehrere Devices parallel zu bedienen ist Edge-Case
 */

import { useCallback } from 'react';
import { useBrowserState } from '../use-browser-state';
import type { WorkflowInstance, WorkflowStateData } from './workflow-types';

const STORAGE_KEY = 'kontora_workflow_state_v1';
const URL_PARAM = 'wf';

type WorkflowStore = Record<string, WorkflowInstance>;

const DEFAULT_STORE: WorkflowStore = {};

function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

export function parseWorkflowStore(raw: string | null | undefined): WorkflowStore {
  if (!raw) return { ...DEFAULT_STORE };
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (typeof parsed !== 'object' || parsed === null) return { ...DEFAULT_STORE };
    const out: WorkflowStore = {};
    for (const [code, value] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof value !== 'object' || value === null) continue;
      const v = value as Partial<WorkflowInstance>;
      if (v.workflowCode !== code) continue;
      if (typeof v.currentStep !== 'number' || v.currentStep < 0) continue;
      if (!isStringRecord(v.data)) continue;
      out[code] = {
        workflowCode: code,
        currentStep: v.currentStep,
        data: v.data,
        startedAt: typeof v.startedAt === 'string' ? v.startedAt : new Date().toISOString(),
        updatedAt: typeof v.updatedAt === 'string' ? v.updatedAt : new Date().toISOString(),
      };
    }
    return out;
  } catch {
    return { ...DEFAULT_STORE };
  }
}

function isStringRecord(value: unknown): value is WorkflowStateData {
  if (typeof value !== 'object' || value === null) return false;
  for (const v of Object.values(value as Record<string, unknown>)) {
    if (typeof v !== 'string') return false;
  }
  return true;
}

function readStore(): WorkflowStore {
  if (!isBrowser()) return { ...DEFAULT_STORE };
  return parseWorkflowStore(window.localStorage.getItem(STORAGE_KEY));
}

function isQuotaError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const name = error.name;
  if (name === 'QuotaExceededError' || name === 'NS_ERROR_DOM_QUOTA_REACHED') return true;
  return error.message.toLowerCase().includes('quota');
}

function pruneCompletedInstances(store: WorkflowStore): WorkflowStore {
  const next: WorkflowStore = {};
  for (const [code, instance] of Object.entries(store)) {
    if (instance.currentStep > 0 || Object.keys(instance.data).length > 0) {
      next[code] = instance;
    }
  }
  return next;
}

function writeStore(store: WorkflowStore): void {
  if (!isBrowser()) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch (error) {
    if (isQuotaError(error)) {
      const pruned = pruneCompletedInstances(store);
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(pruned));
        return;
      } catch (retryError) {
        if (typeof console !== 'undefined') {
          // eslint-disable-next-line no-console
          console.warn('[workflow-state] localStorage quota exceeded, state not persisted', retryError);
        }
        return;
      }
    }
    if (typeof console !== 'undefined') {
      // eslint-disable-next-line no-console
      console.warn('[workflow-state] failed to persist state', error);
    }
  }
}

/**
 * Liefert einen Workflow-Code aus der URL (`?wf=<code>`).
 * Nützlich für Resume-Links aus Emails.
 */
export function getWorkflowFromUrl(): string | null {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  return params.get(URL_PARAM);
}

/**
 * Setzt oder entfernt den `?wf=<code>` URL-Parameter (shallow history).
 */
export function setWorkflowInUrl(code: string | null): void {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  if (code) {
    url.searchParams.set(URL_PARAM, code);
  } else {
    url.searchParams.delete(URL_PARAM);
  }
  window.history.replaceState({}, '', url.toString());
}

export function useWorkflowState(workflowCode: string | null) {
  const [store, setStore, isReady] = useBrowserState(readStore, DEFAULT_STORE);

  const update = useCallback((updater: (prev: WorkflowStore) => WorkflowStore) => {
    setStore((prev) => {
      const next = updater(prev);
      writeStore(next);
      return next;
    });
  }, [setStore]);

  const startWorkflow = useCallback(
    (code: string): WorkflowInstance => {
      const now = new Date().toISOString();
      const instance: WorkflowInstance = {
        workflowCode: code,
        currentStep: 0,
        data: {},
        startedAt: now,
        updatedAt: now,
      };
      // Persist synchronously: zukünftige Mounts (Runner auf der Zielseite)
      // müssen den Workflow sofort finden können – bevor React den State-
      // Update aus dem setStore-Callback committet.
      const previous = readStore();
      writeStore({ ...previous, [code]: instance });
      setStore((prev) => ({ ...prev, [code]: instance }));
      setWorkflowInUrl(code);
      return instance;
    },
    [setStore],
  );

  const setStep = useCallback(
    (code: string, step: number) => {
      update((prev) => {
        const existing = prev[code];
        if (!existing) return prev;
        return {
          ...prev,
          [code]: {
            ...existing,
            currentStep: step,
            updatedAt: new Date().toISOString(),
          },
        };
      });
    },
    [update],
  );

  const recordAnswer = useCallback(
    (code: string, stepId: string, value: string) => {
      update((prev) => {
        const existing = prev[code];
        if (!existing) return prev;
        return {
          ...prev,
          [code]: {
            ...existing,
            data: { ...existing.data, [stepId]: value },
            updatedAt: new Date().toISOString(),
          },
        };
      });
    },
    [update],
  );

  const completeWorkflow = useCallback(
    (code: string) => {
      update((prev) => {
        const next = { ...prev };
        delete next[code];
        return next;
      });
      setWorkflowInUrl(null);
    },
    [update],
  );

  const cancelWorkflow = useCallback(
    (code: string) => {
      update((prev) => {
        const next = { ...prev };
        delete next[code];
        return next;
      });
      setWorkflowInUrl(null);
    },
    [update],
  );

  const resetAll = useCallback(() => {
    update(() => ({ ...DEFAULT_STORE }));
    setWorkflowInUrl(null);
  }, [update]);

  const current = workflowCode ? store[workflowCode] ?? null : null;

  return {
    store,
    isReady,
    current,
    startWorkflow,
    setStep,
    recordAnswer,
    completeWorkflow,
    cancelWorkflow,
    resetAll,
  };
}

/**
 * Liefert den "aktiven" Workflow aus dem Store – den mit dem jüngsten
 * updatedAt. Nützlich für Komponenten, die ohne expliziten Code den
 * laufenden Workflow anzeigen wollen (z. B. globaler WorkflowRunner).
 */
export function pickActiveWorkflow(
  store: WorkflowStore,
  preferredCode?: string | null,
): WorkflowInstance | null {
  if (preferredCode && store[preferredCode]) {
    return store[preferredCode] ?? null;
  }
  const entries = Object.values(store);
  if (entries.length === 0) return null;
  return entries.reduce<WorkflowInstance | null>((latest, current) => {
    if (!latest) return current;
    return new Date(current.updatedAt).getTime() >
      new Date(latest.updatedAt).getTime()
      ? current
      : latest;
  }, null);
}
